import React, { createContext, useState, useCallback, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Spinner } from "reactstrap";

interface ModalProps {
  title?: React.ReactNode;
  message: React.ReactNode;
  submitButtonText?: string;
  submitButtonClass?: string;
  cancelButtonText?: string;
  isHiddenClose?: boolean;
  isShowIconClose?: boolean;
  loading?: boolean;
  loadingClose?: boolean;
  width?: string;
}

interface ModalContextType {
  showModal: (props: ModalProps, onSubmit: () => void, onClose?: () => void) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
}

export const ModalProvider = ({ children }: ProviderProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [setting, setSetting] = useState({} as ModalProps);
  const [onSubmit, setOnSubmit] = useState<() => void>(() => { });
  const [onClose, setOnClose] = useState<() => void>(() => { });

  const showModal = useCallback((props: ModalProps, onSubmit: () => void, onClose?: () => void) => {
    setSetting(props);
    setOnSubmit(() => onSubmit);
    setOnClose(() => onClose);
    setOpen(true);
  }, []);

  const handleConfirm = async () => {
    try {
      setSetting((prev) => ({ ...prev, loading: true }));
      await onSubmit();
      setOpen(false);
    } catch (error) {
      setSetting((prev) => ({ ...prev, loading: false }));
    } finally {
      setSetting((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleClose = async () => {
    if (!onClose) {
      setOpen(false);
      return;
    }
    try {
      setSetting((prev) => ({ ...prev, loadingClose: true }));
      await onClose();
      setOpen(false);
    } catch (error) {
      setSetting((prev) => ({ ...prev, loadingClose: false }));
    } finally {
      setSetting((prev) => ({ ...prev, loadingClose: false }));
    }
  };

  return (
    <ModalContext.Provider value={{ showModal }}>
      {children}
      <Modal
        isOpen={open}
        centered={true}
        onClose={handleClose}
        scrollable={false}
        style={{ width: setting.width || "321px" }}
      >
        <div className="p-3 relative">
          <div
            style={{
              color: "#222",
              fontSize: "18px",
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            {setting.title || t("cms_media.check")}
          </div>
          {
            setting?.isShowIconClose ? <div style={{ position: "absolute", right: "5px", top: "8px" }} onClick={() => setOpen(false)}>
              <i
                className="ri-close-line"
                style={{ fontSize: "20px", cursor: "pointer" }}
              ></i>
            </div> : <></>
          }
          <div
            style={{ marginTop: "10px", color: "#5F646F", textAlign: "center" }}
          >
            {setting.message}
          </div>
          <div
            style={{
              marginTop: "30px",
              gap: "10px",
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {!setting.isHiddenClose && (
              <button
                type="button"
                className="btn btn-secondary w-100"
                onClick={handleClose}
              >
                {setting.loadingClose && <Spinner size="sm" className='me-2' />}
                {setting.cancelButtonText || t("cms_media.cancellation")}
              </button>
            )}
            <button
              type="button"
              className={`btn ${setting.submitButtonClass || "btn-primary"} w-100`}
              onClick={handleConfirm}
            >
              {setting.loading && <Spinner size="sm" className='me-2' />}
              {setting.submitButtonText || t("cms_media.save")}
            </button>
          </div>
        </div>
      </Modal>
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = React.useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};
